import { isPromise } from '@dimjs/lang';
import { core } from '../core/index.js';
import { logError } from '../utils/log.js';

/**
 * 注册当前webview的onShow, onHide
 * 考虑场景 WebviewA->webviewB, webviewB 退出 WebviewA, webviewA 再次打开WebviewB.
 * 1. 启动webviewA, A 执行onload, 当A 打开 B,  则A执行onHide, B执行onLoad
 * 2. B返回 A, B执行onDestroy, A执行onShow, A再此打开B, A执行onHide, B执行onLoad.
 * 3. 直到A再返回, A执行onDestory
 * 4. 进入后台执行onHide, 后台进入前台执行onShow
 */
export type FabricWebviewSubscriptionOptions = {
  /**
   * webview处于激活状态的时候执行. 第一次打开不执行onShow只执行onLoad
   */
  onShow?: (() => void) | (() => Promise<void>);
  /**
   * 当前webview实例处于非激活态（切换到后台、手机熄屏、页面前进等）
   */
  onHide?: (() => void) | (() => Promise<void>);
  /**
   * 页面第一次加载完毕后执行,首次执行, 每次webview只执行一次
   */
  onLoad?: (() => void) | (() => Promise<void>);
  /**
   * 切换到后台时触发
   */
  onBackground?: (() => void) | (() => Promise<void>);
  /**
   * iOS特有, 当未禁用backGesture的时候, 可以尝试调用backGesture功能.
   * 注意此处的callback 不要做网络请求, 以及其他异步耗时较长的逻辑
   * Note *通常此处用来调用 `sendCrossWebviewCall(delta值需要手动减1, 因为动画结束栈已经退出)`*
   * @returns
   */
  onIosBackGesture?: (() => void) | (() => Promise<void>);
  /**
   * 用户触发截屏时触发（Android需要在APP启动时检查文件路径读取权限，有权限再初始化截屏监听）
   */
  onScreenShot?:
    | ((res?: { webviewActive: boolean }) => void)
    | (() => Promise<void>);
};

export type FabricWebviewSubscriptionApi = (
  options: FabricWebviewSubscriptionOptions
) => {
  off: () => void;
};

type SubscriptionMethods = keyof FabricWebviewSubscriptionOptions;

// Cache all handlers
const subscriptionHandlers: Array<FabricWebviewSubscriptionOptions | null> = [];

/**
 * Native事件回调.
 * @param onHandler 回调处理器.
 * @param once default false
 * @returns
 */
const onCallback = (method: SubscriptionMethods, once = false) => {
  return (res, responseCallback) => {
    responseCallback({ code: '0', data: '' });
    for (const handlerMap of subscriptionHandlers) {
      // 可能被off掉了, 此时可能为null.
      if (!handlerMap) {
        continue;
      }
      try {
        const onHandler = handlerMap[method];
        if (onHandler) {
          // FIXME:只执行一次, 强制销毁比如`onLoad`, 避免android因为ready时机导致执行2次.
          if (once) {
            delete handlerMap[method];
          }
          const result = onHandler(res);
          if (isPromise(result)) {
            result.catch((err) => {
              logError('subscription().async onHandler error', err);
            });
          }
        }
      } catch (err) {
        logError('subscription().sync onHandler error', err);
      }
    }
  };
};

/**
 * 启动订阅webview show, hide状态, native 需自行判断客户端webview 是否注册过fabric_webview.onShow.
 * 避免内存泄漏. webview退出需要清理pending 到queue的message
 * @example
 * ```tsx
 *   useEffect(() => {
 *      ready(()=> {
 *        fabricWebview.subscription({
 *           onShow: (() => void) | (() => Promise<void>);
 *           onHide: (() => void) | (() => Promise<void>);
 *           onLoad: (() => void) | (() => Promise<void>);
 *           onIosBackGesture: (() => void) | (() => Promise<void>);
 *        })
 *      })
 *   },[])
 * ```
 */
export const subscription: FabricWebviewSubscriptionApi = (
  config: FabricWebviewSubscriptionOptions
) => {
  // 记录当前订阅的config的start位置.
  const handlerMapIndex = subscriptionHandlers.length;

  // 缓存当前webview所有的handlers
  subscriptionHandlers.push(config);

  // onLoad: 初始化立即调用, 如果已经被调用, 则需要销毁.
  core.registerAsync<string, string>(
    'fabric_webview.onLoad',
    onCallback('onLoad', true)
  );

  // onShow, 应该是always 在ready之后, 所以不需要销毁
  core.registerAsync<string, string>(
    'fabric_webview.onShow',
    onCallback('onShow')
  );

  // onHide, 应该是always 在ready之后, 所以不需要销毁
  core.registerAsync<string, string>(
    'fabric_webview.onHide',
    onCallback('onHide')
  );

  // onBackground, 应该是always 在ready之后, 所以不需要销毁
  core.registerAsync<string, string>(
    'fabric_webview.onBackground',
    onCallback('onBackground')
  );

  // onIosBackGesture, 应该是always 在ready之后, 所以不需要销毁
  core.registerAsync<string, string>(
    'fabric_webview.onBackGesture',
    onCallback('onIosBackGesture')
  );

  // onScreenShot, 应该是always 在ready之后, 所以不需要销毁
  core.registerAsync<string, string>(
    'fabric_webview.onScreenShot',
    onCallback('onScreenShot')
  );

  return {
    off: () => {
      subscriptionHandlers.splice(handlerMapIndex, 1, null);
    },
  };
};

// Makesure we have always subscription above events.
subscription({}).off();
